// Seller Users screen API (/seller/team). installApiAuth() attaches the bearer
// to API-base URLs, so plain fetch is the established pattern here.
import { apiUrl } from '../lib/api'

export type SellerTeamRow = {
  kind: 'member' | 'invitation'
  id: string
  display_name: string | null
  email: string
  role: 'owner' | 'staff'
  status: 'active' | 'invited'
  invited_at: string | null
  expires_at: string | null
  // Staff member (or pending invitee) sees no money amounts.
  prices_hidden?: boolean
  // Hidden by PEXIFY: the Owner cannot turn prices back on.
  prices_locked?: boolean
  // Only on invite/resend responses: false = saved, but the email failed.
  email_sent?: boolean | null
}
export type SellerTeamResponse = { rows: SellerTeamRow[]; can_manage: boolean }

export class SellerTeamApiError extends Error {
  status: number
  code: string | undefined
  constructor(status: number, code: string | undefined) {
    super(code ?? `HTTP ${status}`)
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(`/seller/team${path}`), {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new SellerTeamApiError(res.status, body?.detail?.code)
  }
  return (res.status === 204 ? undefined : await res.json()) as T
}

export const fetchSellerTeam = () => call<SellerTeamResponse>('')
export const inviteStaff = ({ email, hidePrices }: { email: string; hidePrices: boolean }) =>
  call<SellerTeamRow>('/invitations', {
    method: 'POST',
    body: JSON.stringify({ email, hide_prices: hidePrices }),
  })
export const resendInvite = (id: string) =>
  call<SellerTeamRow>(`/invitations/${id}/resend`, { method: 'POST' })
export const revokeInvite = (id: string) =>
  call<void>(`/invitations/${id}/revoke`, { method: 'POST' })
export const removeStaff = (membershipId: string) =>
  call<void>(`/members/${membershipId}`, { method: 'DELETE' })
export const setStaffPricesHidden = ({ id, hidden }: { id: string; hidden: boolean }) =>
  call<void>(`/members/${id}/prices`, { method: 'PUT', body: JSON.stringify({ hidden }) })
